import { supabase } from '../lib/supabase';
import type { PlayerCard, RatingBreakdown } from '../types/playerCard';

/**
 * Service for managing Player Cards
 * Handles CRUD operations for player cards and rating calculations
 */
class PlayerCardService {
  /**
   * Get or create player card for a player
   * Auto-creates card if it doesn't exist
   */
  async getOrCreatePlayerCard(
    playerId: string,
    teamId: string
  ): Promise<PlayerCard> {
    // Try to get existing card
    const { data: existing, error: fetchError } = await supabase
      .from('player_cards')
      .select(`
        *,
        player:players(*)
      `)
      .eq('player_id', playerId)
      .eq('team_id', teamId)
      .single();

    if (existing && !fetchError) {
      return existing;
    }

    // Create new card with defaults
    const { data: newCard, error: createError } = await supabase
      .from('player_cards')
      .insert({
        player_id: playerId,
        team_id: teamId,
        health_status: 'healthy',
        can_play_line: false,
        can_play_skill: false,
        offense_only: false,
        defense_only: false,
        line_only: false,
        skill_only: false,
        no_contact: false,
        limited_contact: false,
        is_new_player: false,
        eligible_positions: [],
        role_types: [],
      })
      .select(`
        *,
        player:players(*)
      `)
      .single();

    if (createError) {
      console.error('[PlayerCardService] Error creating player card:', createError);
      throw createError;
    }

    return newCard;
  }

  /**
   * Get player card by ID
   */
  async getPlayerCard(cardId: string): Promise<PlayerCard | null> {
    const { data, error } = await supabase
      .from('player_cards')
      .select(`
        *,
        player:players(*)
      `)
      .eq('id', cardId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Not found
      }
      console.error('[PlayerCardService] Error fetching player card:', error);
      throw error;
    }

    return data;
  }

  /**
   * Get player card by player ID and team ID
   */
  async getPlayerCardByPlayer(
    playerId: string,
    teamId: string
  ): Promise<PlayerCard | null> {
    const { data, error } = await supabase
      .from('player_cards')
      .select(`
        *,
        player:players(*)
      `)
      .eq('player_id', playerId)
      .eq('team_id', teamId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // Not found
      }
      console.error('[PlayerCardService] Error fetching player card:', error);
      throw error;
    }

    return data;
  }

  /**
   * Get all player cards for a team
   */
  async getPlayerCardsForTeam(teamId: string): Promise<PlayerCard[]> {
    const { data, error } = await supabase
      .from('player_cards')
      .select(`
        *,
        player:players(*)
      `)
      .eq('team_id', teamId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('[PlayerCardService] Error fetching player cards:', error);
      throw error;
    }

    return data || [];
  }

  /**
   * Create a new player card
   */
  async createPlayerCard(
    playerId: string,
    teamId: string,
    cardData: Partial<PlayerCard>
  ): Promise<PlayerCard> {
    const { data, error } = await supabase
      .from('player_cards')
      .insert({
        player_id: playerId,
        team_id: teamId,
        health_status: 'healthy',
        can_play_line: false,
        can_play_skill: false,
        offense_only: false,
        defense_only: false,
        line_only: false,
        skill_only: false,
        no_contact: false,
        limited_contact: false,
        is_new_player: false,
        eligible_positions: [],
        role_types: [],
        ...cardData,
      })
      .select()
      .single();

    if (error) {
      console.error('[PlayerCardService] Error creating player card:', error);
      throw error;
    }

    return data;
  }

  /**
   * Update player card
   */
  async updatePlayerCard(
    cardId: string,
    updates: Partial<PlayerCard>
  ): Promise<PlayerCard> {
    const { data, error } = await supabase
      .from('player_cards')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('id', cardId)
      .select()
      .single();

    if (error) {
      console.error('[PlayerCardService] Error updating player card:', error);
      throw error;
    }

    return data;
  }

  /**
   * Delete player card
   */
  async deletePlayerCard(cardId: string): Promise<void> {
    const { error } = await supabase
      .from('player_cards')
      .delete()
      .eq('id', cardId);

    if (error) {
      console.error('[PlayerCardService] Error deleting player card:', error);
      throw error;
    }
  }

  /**
   * Calculate overall rating for a player card (0-10 scale, legacy)
   * Uses weighted average of physical, technical, and mental attributes
   */
  async calculateRating(
    cardId: string,
    sport: string
  ): Promise<number> {
    // Get all attributes for this sport
    const { data: attributes, error: attrError } = await supabase
      .from('player_attributes')
      .select('*')
      .eq('player_card_id', cardId)
      .eq('sport', sport);

    if (attrError) {
      console.error('[PlayerCardService] Error fetching attributes:', attrError);
      throw attrError;
    }

    // Get attendance stats for attendance factor
    const { data: card } = await supabase
      .from('player_cards')
      .select('player_id, team_id')
      .eq('id', cardId)
      .single();

    if (!card) {
      throw new Error('Player card not found');
    }

    // Calculate category averages
    const physicalAttrs = attributes?.filter(a => a.category === 'physical') || [];
    const technicalAttrs = attributes?.filter(a => a.category === 'technical') || [];
    const mentalAttrs = attributes?.filter(a => a.category === 'mental') || [];

    const physicalAvg = physicalAttrs.length > 0
      ? physicalAttrs.reduce((sum, a) => sum + a.value, 0) / physicalAttrs.length
      : 5.0; // Default to middle if no attributes

    const technicalAvg = technicalAttrs.length > 0
      ? technicalAttrs.reduce((sum, a) => sum + a.value, 0) / technicalAttrs.length
      : 5.0;

    const mentalAvg = mentalAttrs.length > 0
      ? mentalAttrs.reduce((sum, a) => sum + a.value, 0) / mentalAttrs.length
      : 5.0;

    // Get attendance factor (0.8 to 1.0 multiplier)
    // This would ideally come from attendance service, but for now use default
    const attendanceFactor = 1.0; // TODO: Integrate with attendance service

    // Weighted calculation (can be tuned)
    // Physical: 30%, Technical: 50%, Mental: 20%
    const baseRating = (physicalAvg * 0.3) + (technicalAvg * 0.5) + (mentalAvg * 0.2);
    const overall = Math.min(10.0, Math.max(0.0, baseRating * attendanceFactor));

    const ratingBreakdown: RatingBreakdown = {
      physical_score: Math.round(physicalAvg * 10) / 10,
      technical_score: Math.round(technicalAvg * 10) / 10,
      mental_score: Math.round(mentalAvg * 10) / 10,
      attendance_factor: attendanceFactor,
      overall: Math.round(overall * 10) / 10,
      last_calculated: new Date().toISOString(),
    };

    // Update card with rating
    const { error: updateError } = await supabase
      .from('player_cards')
      .update({
        overall_rating: ratingBreakdown.overall,
        rating_breakdown: ratingBreakdown,
        updated_at: new Date().toISOString(),
      })
      .eq('id', cardId);

    if (updateError) {
      console.error('[PlayerCardService] Error updating rating:', updateError);
      throw updateError;
    }

    return ratingBreakdown.overall;
  }

  /**
   * Calculate OVR (Overall Rating) for a player card (0-100 scale, Madden-style)
   * Position-aware weighted calculation based on attributes
   * Only includes attributes that are actually set (optional attributes don't penalize)
   */
  async calculateOVR(
    cardId: string,
    sport: string
  ): Promise<number> {
    // Get all attributes for this sport
    const { data: attributes, error: attrError } = await supabase
      .from('player_attributes')
      .select('*')
      .eq('player_card_id', cardId)
      .eq('sport', sport);

    if (attrError) {
      console.error('[PlayerCardService] Error fetching attributes:', attrError);
      throw attrError;
    }

    // Get card with position info
    const { data: card } = await supabase
      .from('player_cards')
      .select('eligible_positions, role_types')
      .eq('id', cardId)
      .single();

    if (!card) {
      throw new Error('Player card not found');
    }

    if (!attributes || attributes.length === 0) {
      // No attributes = default OVR of 50
      await supabase
        .from('player_cards')
        .update({ ovr_rating: 50, updated_at: new Date().toISOString() })
        .eq('id', cardId);
      return 50;
    }

    // Import attribute definitions to check required vs optional
    const { getRequiredTechnicalAttributes, getOptionalTechnicalAttributes } = await import('../types/playerCard');
    
    // Separate attributes by category
    const physicalAttrs = attributes.filter(a => a.category === 'physical') || [];
    const technicalAttrs = attributes.filter(a => a.category === 'technical') || [];
    const mentalAttrs = attributes.filter(a => a.category === 'mental') || [];

    // For football, only count required technical attributes + any optional ones that are set
    // This prevents optional attributes from penalizing players who don't need them
    let technicalAttrsForCalculation = technicalAttrs;
    if (sport === 'Football') {
      const required = getRequiredTechnicalAttributes(sport);
      const optional = getOptionalTechnicalAttributes(sport);
      
      // Only include optional attributes if they're actually set (not default/empty)
      // For now, we'll include all set attributes, but we could filter by position eligibility
      technicalAttrsForCalculation = technicalAttrs.filter(attr => {
        // Always include required attributes
        if (required.includes(attr.attribute_key)) {
          return true;
        }
        // Include optional attributes if they're set (value > 0, or if we want to be more strict, > default)
        if (optional.includes(attr.attribute_key)) {
          return attr.value > 0; // Only count if explicitly set
        }
        return false;
      });
    }

    // Calculate category averages (1-10 scale)
    // Only use attributes that are actually set
    const physicalAvg = physicalAttrs.length > 0
      ? physicalAttrs.reduce((sum, a) => sum + a.value, 0) / physicalAttrs.length
      : 5.0;

    const technicalAvg = technicalAttrsForCalculation.length > 0
      ? technicalAttrsForCalculation.reduce((sum, a) => sum + a.value, 0) / technicalAttrsForCalculation.length
      : 5.0;

    const mentalAvg = mentalAttrs.length > 0
      ? mentalAttrs.reduce((sum, a) => sum + a.value, 0) / mentalAttrs.length
      : 5.0;

    // Position-aware weighting (can be tuned per sport/position)
    // For now, use general weights: Physical 35%, Technical 45%, Mental 20%
    // This creates a 0-10 scale, then convert to 0-100
    const baseOVR = (physicalAvg * 0.35) + (technicalAvg * 0.45) + (mentalAvg * 0.20);
    
    // Convert from 0-10 scale to 0-100 scale (Madden-style)
    const ovr = Math.round(baseOVR * 10);
    const clampedOVR = Math.min(100, Math.max(0, ovr));

    // Update card with OVR
    const { error: updateError } = await supabase
      .from('player_cards')
      .update({
        ovr_rating: clampedOVR,
        updated_at: new Date().toISOString(),
      })
      .eq('id', cardId);

    if (updateError) {
      console.error('[PlayerCardService] Error updating OVR:', updateError);
      throw updateError;
    }

    return clampedOVR;
  }

  /**
   * Update position eligibility
   */
  async updatePositionEligibility(
    cardId: string,
    eligiblePositions: string[]
  ): Promise<PlayerCard> {
    return this.updatePlayerCard(cardId, {
      eligible_positions: eligiblePositions,
    });
  }

  /**
   * Update role types
   */
  async updateRoleTypes(
    cardId: string,
    roleTypes: string[]
  ): Promise<PlayerCard> {
    return this.updatePlayerCard(cardId, {
      role_types: roleTypes,
    });
  }

  /**
   * Update health status
   */
  async updateHealthStatus(
    cardId: string,
    status: 'healthy' | 'limited' | 'no_contact',
    notes?: string
  ): Promise<PlayerCard> {
    return this.updatePlayerCard(cardId, {
      health_status: status,
      health_notes: notes,
      health_updated_at: new Date().toISOString(),
    });
  }
}

export const playerCardService = new PlayerCardService();

